import { Directive, ElementRef, HostListener, Input, OnInit, Renderer2 } from '@angular/core';

/**
 * Tooltip button ("for tooltip") directive
 *
 * @description Defines an element with a tooltip. The element containing this directive should be placed immediately before `<lsq-tooltip>`. Takes a string equal to the `id` of the `lsq-tooltip` element.
 * @see LsqTooltipComponent
 * @export
 * @class LsqHasTooltipDirective
 * @implements {OnInit}
 *
 * @property {string} lsqHasTooltip A string equal to the `id` of the `lsq-tooltip` element.
 * @property {boolean} [hover] If `false`, tooltip will only display on click; otherwise, tooltip will only display on hover. Defaults to `true`.
 * @property {boolean} [click] If `true`, tooltip will toggle on click. Defaults to `false`.
 *
 * @example
 * <strong id="my-tooltip-button" lsqHasTooltip="my-tooltip">Hover me</strong>
 * <lsq-tooltip for="my-tooltip-button" [id]="'my-tooltip'" orientation="bottom">Tooltip!</lsq-tooltip>
 */
@Directive({
    selector: '[lsqHasTooltip]'
})
export class LsqHasTooltipDirective implements OnInit {
    /**
     * The `lsqHasTooltip` input.
     *
     * @type {string}
     * @memberof LsqHasTooltipDirective
     * @alias lsqHasTooltip
     */
    @Input('lsqHasTooltip') public tooltipId: string;
    @Input() public hover? = true;
    @Input() public click? = false;
    private nativeElement: HTMLElement;
    private tooltipIsActive = false;

    constructor(private renderer: Renderer2, private elementRef: ElementRef) {
        this.nativeElement = this.elementRef.nativeElement;
        this.renderer.setAttribute(this.nativeElement, 'aria-haspopup', 'true');
        this.renderer.setAttribute(this.nativeElement, 'tabindex', '0');
    }

    public ngOnInit() {
        if (!this.hover) {
            this.click = true;
        }
    }

    @HostListener('mouseover')
    public onMouseOver() {
        if (this.hover) {
            this.show();
        }
    }

    @HostListener('mouseout')
    public onMouseOut() {
        if (this.hover) {
            this.hide();
        }
    }

    @HostListener('click')
    public onClick() {
        if (this.click) {
            if (!this.tooltipIsActive) {
                this.show();
            } else {
                this.hide();
            }
        }
    }

    private show() {
        this.tooltipIsActive = true;
        this.renderer.setAttribute(this.nativeElement, 'aria-expanded', 'true');
        this.renderer.setAttribute(this.nativeElement, 'aria-controls', this.tooltipId);
    }

    private hide() {
        this.tooltipIsActive = false;
        this.renderer.setAttribute(this.nativeElement, 'aria-expanded', 'false');
        this.renderer.removeAttribute(this.nativeElement, 'aria-controls');
        this.renderer.addClass(this.nativeElement, 'fading-out');
        setTimeout(() => {
            this.renderer.removeClass(this.nativeElement, 'fading-out');
        }, 300);
    }
}
